// The four boxes of a new outcome description: Field (output format), Captures, Rules, NA / default.
// The server builds the text PCA reads from the boxes (techladder-core outcome_validation/sections.py);
// compileSections below only mirrors it for the live character count and preview. The two must agree:
// outcome-sections.test.ts checks this against the same example the server tests use.

export interface OutcomeSections {
  captures: string;
  field_format: string;
  rules: string;
  na_default: string;
}

export type SectionKey = keyof OutcomeSections;

export interface SectionBox {
  key: SectionKey;
  label: string;
  heading: string;
  max: number;
  rows: number;
  hint: string;
}

// Order and limits match the server. The limits keep the built text under the 4000-character description limit.
export const SECTION_BOXES: SectionBox[] = [
  {
    key: "captures",
    label: "What it captures",
    heading: "WHAT THIS FIELD CAPTURES",
    max: 600,
    rows: 2,
    hint: "Which question about the call this answers, and whose words count (the customer's own words, not the agent's).",
  },
  {
    key: "field_format",
    label: "Output format",
    heading: "OUTPUT FORMAT",
    max: 400,
    rows: 2,
    hint: "The exact output: spelling, case, digits or date format. The allowed values themselves are set above.",
  },
  {
    key: "rules",
    label: "Rules",
    heading: "RULES",
    max: 2200,
    rows: 6,
    hint: "One rule per allowed value: example phrases that mean it, what does NOT count, and which agent question a yes/no answers. If the answer can change during the call, say which one wins.",
  },
  {
    key: "na_default",
    label: "NA / default",
    heading: "NA / DEFAULT",
    max: 600,
    rows: 2,
    hint: "When the answer is NA or the default, and what to output when nothing matches. Never pick the closest value.",
  },
];

export const EMPTY_SECTIONS: OutcomeSections = { captures: "", field_format: "", rules: "", na_default: "" };

export function compileSections(sections: Partial<OutcomeSections> | null | undefined): string {
  const parts: string[] = [];
  for (const box of SECTION_BOXES) {
    const text = String(sections?.[box.key] ?? "").trim();
    if (!text) continue;
    parts.push(box.key === "rules" ? `${box.heading}:\n${text}` : `${box.heading}: ${text}`);
  }
  return parts.join("\n\n");
}

export function sectionsAreBlank(sections: Partial<OutcomeSections> | null | undefined): boolean {
  return SECTION_BOXES.every((box) => !String(sections?.[box.key] ?? "").trim());
}

interface MaybeFourBox {
  description?: string;
  sections?: Partial<OutcomeSections> | null;
}

// New outcomes (nothing written yet) and outcomes already written in boxes use the four boxes.
// An old outcome with plain text stays a text box; it is never converted automatically.
export function usesFourBoxes(item: MaybeFourBox): boolean {
  return Boolean(item.sections) || !String(item.description ?? "").trim();
}

// Offered, never forced: copies the old text into the Rules box so the author can split it up.
export function switchToFourBoxes(item: MaybeFourBox): { sections: OutcomeSections; description: string } {
  const sections: OutcomeSections = { ...EMPTY_SECTIONS, rules: String(item.description ?? "").slice(0, 2200) };
  return { sections, description: compileSections(sections) };
}
